import { useRef, useState, TouchEvent } from "react";

const SWIPE_CLOSE_THRESHOLD = 80;

const useHandleGlobalPopupSwipe = (onClose: () => void) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragY, setDragY] = useState(0);
    const dragStartY = useRef<number | null>(null);
    const swipeAborted = useRef<boolean>(false);
    const popupRef = useRef<HTMLDivElement | null>(null);

    const handleTouchStart = (e: TouchEvent<HTMLElement>) => {
        if (!popupRef.current) return;
        
        // Check if there's scrollable content and if we're at the top
        const scrollableContent = popupRef.current.querySelector('[data-scrollable]') as HTMLElement;
        if (scrollableContent && scrollableContent.scrollTop > 0) {
            return; // Don't start swipe if content is scrolled
        }
        
        swipeAborted.current = false;
        setIsDragging(true);
        dragStartY.current = e.touches[0].clientY;
        setDragY(0);
    };

    const handleTouchMove = (e: TouchEvent<HTMLElement>) => {
        if (!isDragging || swipeAborted.current) return;
        if (!popupRef.current) return;
        
        // Check if content is being scrolled
        const scrollableContent = popupRef.current.querySelector('[data-scrollable]') as HTMLElement;
        if (scrollableContent && scrollableContent.scrollTop > 0) {
            swipeAborted.current = true;
            setIsDragging(false);
            setDragY(0);
            return;
        }
        
        const currentY = e.touches[0].clientY;
        if (dragStartY.current !== null) {
            const delta = currentY - dragStartY.current;
            if (delta > 0) {
                setDragY(delta);
                e.preventDefault(); // Prevent scrolling when swiping down
            }
        }
    };

    const handleTouchEnd = (e: TouchEvent<HTMLElement>) => {
        if (!isDragging) return;
        setIsDragging(false);
        if (swipeAborted.current) {
            setDragY(0);
            return;
        }
        if (dragY > SWIPE_CLOSE_THRESHOLD) {
            onClose();
        } else {
            setDragY(0);
        }
        dragStartY.current = null;
    };

    return {
        popupRef,
        dragY,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd
    };
};

export default useHandleGlobalPopupSwipe; 